<template>
  <div>
    <v-snackbar
      top
      outlined
      v-model="showSnack"
      color="success"
      timeout="1000"
      centered
    >
      {{ snackMessage }}
    </v-snackbar>
    <v-progress-linear v-if="loading" :indeterminate="true"/>
    <v-container class="users" fluid v-if="!loading">
      <v-row flat class="d-print-none">
        <v-col class="text-right" style="font-size: smaller" v-if="metricsShown">
          Current orders are from {{ dateFormatted(dateFrom) }} to {{ dateFormatted(dateTo) }}<br/>
          Upcoming orders are from {{ dateFormatted(upcomingDateFrom) }} to {{ dateFormatted(upcomingDateTo) }}<br/>
          If you want this page to load faster, hide "First Week/Day", "Current Meals", and "Upcoming Meals"
          <!--          <v-btn outlined @click="adjustDate(-1,'week')">-->
          <!--            <v-icon>mdi-chevron-left</v-icon>-->
          <!--          </v-btn>-->
          <!--          Adjust Dates-->
          <!--          <v-btn outlined @click="adjustDate(1,'week')">-->
          <!--            <v-icon>mdi-chevron-right</v-icon>-->
          <!--          </v-btn>-->
        </v-col>
      </v-row>
      <v-toolbar flat class="d-print-none pb-10">
        <DebouncedTextField
          v-model="search"
          append-icon="mdi-magnify"
          label="Search for customer"
          single-line
          hide-details
          clearable
        />
        <v-btn v-if="canBulkEdit && selected.length>0"
               @click="showBulkEditDialog=true"
               text outlined class="ml-4">Bulk Edit
        </v-btn>
        <v-btn v-if="selected.length>0"
               @click="showEmails=true"
               text outlined class="ml-4">Copy Emails
        </v-btn>
        <v-select
          class="pl-4"
          label="Filter by Plan"
          v-model="filterByPlan"
          :items="plans"
          hide-details
          clearable
        />
        <v-spacer/>
        <v-checkbox
          v-model="showShopify"
          label="Show Shopify"
          hide-details
        />
        <v-checkbox
          v-model="showDeleted"
          label="Show Deleted"
          hide-details
        />
        <template v-if="finance">
          <span>Apply</span>
          <v-checkbox
            style="max-width: 80px"
            persistent-hintcomponent
            v-model="applyFilter"
            type="number"
            hide-details
            true-value="0"
          />
          <v-text-field
            :disabled="!applyFilter"
            reverse
            prefix="days"
            style="max-width: 110px"
            label="Last Invoice Within"
            v-model="daysBack"
            type="number"
            min="1"
            hide-details
          />
          <v-select
            class="pl-4"
            style="max-width: 280px"
            label="Uses"
            v-model="usesPreferences"
            :items="preferenceFilterOptions"
            multiple
            small-chips
            hide-details
            clearable
          />
        </template>
        <v-checkbox
          class="pl-4"
          :label="`Has Meals ${finance ? totalMealCount : ''}`"
          v-model="hasMeals"
          hide-details
        />
        <v-checkbox
          class="pl-4"
          label="Show Unconfirmed"
          v-model="includeUnconfirmed"
          hide-details
        />

        <v-spacer/>
        <!--        <v-combobox-->
        <!--            label="View"-->
        <!--            single-line-->
        <!--            hide-details-->
        <!--            style="max-width: 160px"-->
        <!--            v-model="viewName"-->
        <!--            :items="views"-->
        <!--            @change="saveSettings"-->
        <!--        />-->

        <v-select
          v-if="savedFilterNames.length"
          class="mr-2"
          style="max-width: 200px"
          label="Saved Filters"
          v-model="selectedSavedFilter"
          :items="savedFilterNames"
          @change="applySavedFilter"
          dense outlined hide-details clearable
        />
        <v-btn outlined small class="mr-2" :color="columnFilters.length ? 'primary' : undefined"
               @click="showFilterDialog=true">
          <v-icon left small>mdi-filter-variant</v-icon>
          Filter{{ columnFilters.length ? ` (${columnFilters.length})` : '' }}
        </v-btn>
        <v-btn outlined small @click="showColumnsDialog=true">
          <v-icon left small>mdi-dots-vertical</v-icon>Columns
        </v-btn>
      </v-toolbar>
      <v-toolbar flat v-if="finance">
        <v-spacer/>
        <v-btn outlined @click="csvExport(dataAsTable,'customers.csv')">Download CSV</v-btn>
      </v-toolbar>
      <div>
        <v-data-table
          :headers="sortedHeaders"
          :items="customers"
          item-key="uid"
          :search="search"
          :loading="loading"
          sort-by="name"
          dense
          calculate-widths
          :show-group-by="customers.length<pagingLimit"
          :expanded.sync="expanded"
          :footer-props="footerProps"
          :group-desc="false"
          :group-by.sync="groupBy"
          :disable-pagination="customers.length<pagingLimit"
          :hide-default-footer="customers.length<pagingLimit"
          :items-per-page="20"
          :no-results-text="applyFilter?`No results found, try unchecking 'Apply Filter'`:undefined"
          show-select
          selectable-key="uid"
          v-model="selected"
        >
          <template v-slot:item.address="{ item } ">
            {{ formatAddress(item.address) }} {{ formatDeliveryTime(item) }}
          </template>
          <template v-slot:item.useAddress="{ item } ">
            <ul v-if="item.useAddress ">
              <li v-for="(e,index) of Object.entries(item.useAddress)" v-bind:key="index">
                <b>{{ e[0] }}</b> {{ formatAddress(e[1]) }} {{ formatDeliveryTime(e[1]) }}
              </li>
            </ul>
          </template>
          <template v-slot:item.stripe_id="{ item } ">
            <a target="_blank" :href="`https://dashboard.stripe.com/${newStripeAccount}/customers/${item.stripe_id}`">
              {{ item.stripe_id }}
            </a>
          </template>
          <template v-slot:item.currentOrderCount="{ item} ">
            <router-link :to="{name:'OrderEdit', query: { customer: item.uid, dates: [dateFrom,dateTo]}}">
              {{ item.currentOrderCount }}
            </router-link>
          </template>
          <template v-slot:item.upcomingOrderCount="{ item} ">
            <router-link
              :to="{name:'OrderEdit', query: { customer: item.uid, dates: [upcomingDateFrom,upcomingDateTo]}}">
              {{ item.upcomingOrderCount }}
            </router-link>
          </template>
          <template v-slot:item.net="{ item} ">
            <v-row>
              <v-col></v-col>
              <v-col>{{ item.net ? `$${(item.net / 100).toFixed(2)}` : '' }}</v-col>
            </v-row>
          </template>
          <template v-slot:item.refunds="{ item} ">
            <v-row>
              <v-col></v-col>
              <v-col>{{ item.refunds ? `$${(item.refunds / 100).toFixed(2)}` : '' }}</v-col>
            </v-row>
          </template>
          <template v-slot:item.total="{ item} ">
            <v-row>
              <v-col></v-col>
              <v-col>{{ item.total ? `$${(item.total / 100).toFixed(2)}` : '' }}</v-col>
            </v-row>
          </template>


          <template v-slot:item.allergies="{ item }">
            <v-chip
              text outlined
              color="red"
              x-small
              class="ma-1"
              v-for="a of item.allergies"
              v-bind:key="a"
            >
              {{ a }}
            </v-chip>
          </template>
          <template v-slot:item.lovedIngredients="{ item }">
            <v-chip text outlined color="green" x-small class="ma-1"
                    v-for="i of item.lovedIngredients" v-bind:key="i">{{ i }}</v-chip>
          </template>
          <template v-slot:item.hatedIngredients="{ item }">
            <v-chip text outlined color="orange" x-small class="ma-1"
                    v-for="i of item.hatedIngredients" v-bind:key="i">{{ i }}</v-chip>
          </template>
          <template v-slot:item.nutritionGoals="{ item }">
            <v-chip text outlined color="blue" x-small class="ma-1"
                    v-for="g of item.nutritionGoals" v-bind:key="g">{{ g }}</v-chip>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn small outlined v-if="!readOnly"
                   :to="{name:(finance?'CustomerFinance':'Customer'), params: { id: item.uid} }">Edit
            </v-btn>
          </template>
          <template v-slot:item.email="{ item }">
            <CustomerHeader :customer="item" :show-name="false"/>
          </template>
          <template v-slot:item.paidByEmails="{ item }">
            <v-btn v-if="item.paidBy.length > 1" icon small>
              <v-icon onclick="alert(`paid by more than one person!`)" color="red">mdi-alert</v-icon>
            </v-btn>
            <span v-if="item.paidByEmails" class="d-inline-block text-truncate caption" style="max-width: 150px">
            <router-link
              v-for="(c,i) of item.paidBy" v-bind:key="i"
              :to="{name:(finance?'CustomerFinance':'Customer'), params: { id: c.uid}}">
              {{ c.email }}
            </router-link>
            </span>
          </template>
          <template v-slot:item.subAccountCount="{ item }">
            <v-btn x-small v-if="item.subCustomers.length>0"
                   @click="expanded = expanded[0]!==item?[item]:[]"
            >
              {{ item.subCustomers.length }} Sub Accounts
            </v-btn>
          </template>
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length">
              <v-simple-table class="ma-0 pa-0">
                <thead>
                <th v-for="header of headers"
                    v-bind:key="header.value"
                ><b>{{ header.text }}</b>
                </th>
                </thead>
                <tbody>
                <tr
                  v-for="subCustomer of item.subCustomers"
                  v-bind:key="subCustomer.uid"
                >
                  <td
                    v-for="header of headers"
                    v-bind:key="header.value"
                  >
                    {{ subCustomer[header.value] }}
                    <v-btn small outlined v-if="header.value==='actions' && !readOnly"
                           :to="{name:(finance?'CustomerFinance':'Customer'), params: { id: item.uid} }">Edit
                    </v-btn>
                  </td>
                </tr>
                </tbody>
              </v-simple-table>
            </td>
          </template>
        </v-data-table>
      </div>
    </v-container>
    <v-dialog v-model="showBulkEditDialog">
      <v-card v-if="savingCount>0">
        <v-card-title>Saving {{ savingCount }}</v-card-title>
        <v-card-actions>
          <v-btn @click="cancelBulkEdit=true">Cancel</v-btn>
          <v-spacer/>
          <v-btn disabled @click="saveBulkEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
      <v-card v-else>
        <v-card-title>Bulk Edit {{ selected.length }} Customers</v-card-title>
        <v-card-text>
          <v-select
            label="Attribute"
            v-model="attributeToEdit"
            :items="customerAttributes"
          />
          <v-select v-if="attributeOptions[attributeToEdit]"
                    v-model="newValue"
                    :items="attributeOptions[attributeToEdit]"
          />
          <v-text-field v-else v-model="newValue"/>

        </v-card-text>
        <v-card-actions>
          <v-btn @click="showBulkEditDialog=false">Cancel</v-btn>
          <v-spacer/>
          <v-btn @click="saveBulkEdit">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEmails">
      <v-card>
        <v-card-title>{{ selected.length }} email addresses shown below</v-card-title>
        <v-card-actions>
          <v-btn @click="showEmails=false">Close</v-btn>
          <v-spacer/>
          <v-btn @click="copyEmails">Copy</v-btn>
        </v-card-actions>
        <v-card-text>
          <v-textarea
            ref="emailsToCopy"
            :value="selected.map(c=>c.email).join('\n')"
            auto-grow
            style="max-height: fit-content"
          />
        </v-card-text>

      </v-card>

    </v-dialog>
    <v-dialog v-model="showColumnsDialog" max-width="1000">
      <v-card>
        <v-card-title>
          Select Columns
          <v-spacer/>
          <v-btn icon @click="showColumnsDialog=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text class="pb-0">
          <v-row align="center" dense>
            <v-col>
              <v-text-field
                v-model="columnSearch"
                placeholder="Search columns..."
                prepend-inner-icon="mdi-magnify"
                clearable
                dense
                outlined
                hide-details
              />
            </v-col>
            <v-col cols="auto">
              <v-btn small text outlined @click="selectAllColumns">Select All</v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn small text outlined @click="clearAllColumns">Clear All</v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text class="py-2">
          <v-row dense>
            <v-col
              v-for="header in filteredColumnHeaders"
              :key="header.value"
              cols="6" sm="4" md="3"
            >
              <v-checkbox
                :label="header.text"
                :input-value="selectedHeaders.some(h => h.value === header.value)"
                @change="toggleColumn(header, $event)"
                hide-details
                dense
                class="mt-0 pt-0"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="selectedHeaders=defaultHeaders;saveSettings()">Reset to Default</v-btn>
          <v-spacer/>
          <v-btn color="primary" @click="showColumnsDialog=false">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showFilterDialog" max-width="900">
      <v-card>
        <v-card-title>
          Filter Rows
          <v-spacer/>
          <v-btn icon @click="showFilterDialog=false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-text v-if="savedFilterNames.length" class="pb-0">
          <div class="caption">Saved filters (click to apply)</div>
          <v-chip
            v-for="name of savedFilterNames" :key="name"
            class="ma-1" small close
            :color="selectedSavedFilter === name ? 'primary' : undefined"
            :outlined="selectedSavedFilter !== name"
            @click="applySavedFilter(name)"
            @click:close="deleteSavedFilter(name)"
          >{{ name }}</v-chip>
        </v-card-text>
        <v-card-text>
          <p class="caption mb-2">Rows must match every filter. Blank means empty, missing or an empty list.</p>
          <v-row v-for="(filter, index) of columnFilters" :key="index" dense align="center">
            <v-col cols="4">
              <v-autocomplete
                v-model="filter.column"
                :items="filterColumnOptions"
                label="Column"
                placeholder="Type to search"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="3">
              <v-select
                v-model="filter.condition"
                :items="filterConditionOptions"
                label="Condition"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="4">
              <v-text-field
                v-model="filter.value"
                :disabled="!filterConditionNeedsValue(filter.condition)"
                label="Value"
                dense outlined hide-details
              />
            </v-col>
            <v-col cols="1">
              <v-btn icon @click="columnFilters.splice(index, 1)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-col>
          </v-row>
          <v-btn text small class="mt-2" @click="addColumnFilter">
            <v-icon left small>mdi-plus</v-icon>Add Filter
          </v-btn>
          <v-row dense align="center" class="mt-4">
            <v-col cols="8">
              <v-text-field
                v-model="savedFilterName"
                label="Save these filters as"
                placeholder="e.g. No goals, has preset"
                dense outlined hide-details
                @keyup.enter="saveCurrentFilters"
              />
            </v-col>
            <v-col cols="4">
              <v-btn small outlined :disabled="!canSaveFilters" @click="saveCurrentFilters">
                <v-icon left small>mdi-content-save</v-icon>Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="columnFilters=[]">Clear All</v-btn>
          <v-spacer/>
          <v-btn color="primary" @click="showFilterDialog=false">Done</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>


<script>
import {mapActions, mapGetters} from 'vuex'
import moment from 'moment';
import api from '@/api';
import {getDateString, dateFormatted, csvAsTable, csvExport, formatAddress} from '@/store/utils';
import urlState from "@/router/urlState";
import DebouncedTextField from "@/components/DebouncedTextField.vue";
import CustomerHeader from "@/components/CustomerHeader";

// Fields the `customersUnfiltered` row transform (and core filters) read for
// EVERY row regardless of which columns are visible — so they must always be
// requested. `address` is also a hard row filter (rows without it are dropped).
// The trailing three are the order-metric fields injected server-side when
// orderMetrics/hasMeals is set; the default `hasMeals` filter and the metric
// columns depend on them, so we request them (harmless when not injected).
// customer ids are the same on both Stripe accounts (the October 2026 copy kept
// them); the live customer is on the new one, so the link names it
const NEW_STRIPE_ACCOUNT = 'acct_1GjcI1D0KVemEdeG';

const ALWAYS_REQUIRED_FIELDS = [
  'uid', 'address', 'first_name', 'last_name', 'email', 'plan', 'isDeleted',
  'subAccounts', 'stats', 'stripe', 'metrics', 'referralCoupons', 'pendingEdits', 'allergies',
  'upcomingOrderCount', 'currentOrderCount', 'isFirstOrder',
];

// Columns whose `value` is not simply the raw top-level field name: maps a
// header value to the raw field(s) that feed it. Anything not listed here and
// without a dot is assumed to be a direct top-level field; a dotted value
// (eg. `address.location`, `mixpanel.last_seen`) requests its top-level parent.
// Empty arrays = the column is populated by orderMetrics/hasMeals, not `fields`.
const COLUMN_FIELD_OVERRIDES = {
  name: ['first_name', 'last_name'],
  pauseFrom: ['pause'],
  pauseTo: ['pause'],
  coupon: ['stripe', 'coupon'],
  deliveryTimes: ['address'],
  addressFormatted: ['address'],
  pendingAddressFormatted: ['pendingEdits'],
  subAccountCount: ['subAccounts'],
  paidByEmails: ['subAccounts', 'email'],
  neverOrdered: ['metrics'],
  referralCredits: ['referralCoupons'],
  invoiceCount: ['stats'],
  lastCharge: ['stats'],
  lastChargeMaster: ['stats', 'subAccounts'],
  lastChargeEither: ['stats', 'subAccounts'],
  isFirstOrder: ['stripe'],
  dietPreset: ['ingredient_preferences', 'pendingEdits'],
  lovedIngredients: ['ingredient_preferences', 'pendingEdits'],
  hatedIngredients: ['ingredient_preferences', 'pendingEdits'],
  nutritionGoals: ['ingredient_preferences', 'pendingEdits'],
  firstOrderDay: [],
  currentOrderCount: [],
  upcomingOrderCount: [],
  stripe_id: ['stripe'],
  total: ['stats'],
  refunds: ['stats'],
  net: ['stats'],
  firstCharge: ['stats'],
  age: ['stats'],
  actions: ['uid'],
};

function columnToFields(value) {
  if (value in COLUMN_FIELD_OVERRIDES) {
    return COLUMN_FIELD_OVERRIDES[value];
  }
  if (value.includes('.')) {
    return [value.split('.')[0]];
  }
  return [value];
}

// Spreadsheet-style row filters: every filter is {column, condition, value}
// where column is a header value; those with needsValue compare against the
// typed value. Serialised to the URL as JSON (`filters` query param).
const FILTER_CONDITIONS = [
  {text: 'is blank', value: 'blank', needsValue: false},
  {text: 'is not blank', value: 'notBlank', needsValue: false},
  {text: 'equals', value: 'equals', needsValue: true},
  {text: 'does not equal', value: 'notEquals', needsValue: true},
  {text: 'contains', value: 'contains', needsValue: true},
  {text: 'does not contain', value: 'notContains', needsValue: true},
  {text: 'starts with', value: 'startsWith', needsValue: true},
  {text: 'greater than', value: 'greaterThan', needsValue: true},
  {text: 'less than', value: 'lessThan', needsValue: true},
];

// A row's cell for a header value: the formatted row's own key first (eg.
// `address.city` is flattened onto the row), else the dotted path into it.
function cellValue(row, column) {
  if (column in row) return row[column];
  return column.split('.').reduce((obj, key) => (obj == null ? undefined : obj[key]), row);
}

function isBlankCell(value) {
  if (value === null || value === undefined) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'string') return value.trim() === '';
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function cellText(value) {
  if (isBlankCell(value)) return '';
  if (Array.isArray(value)) return value.map(cellText).join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// numeric when both sides parse as numbers, else case-insensitive text
function compareCells(cell, expected) {
  const a = Number(cell);
  const b = Number(expected);
  if (cellText(cell) !== '' && expected.trim() !== '' && !isNaN(a) && !isNaN(b)) {
    return a - b;
  }
  return cellText(cell).toLowerCase().localeCompare(expected.toLowerCase());
}

function rowMatchesFilter(row, {column, condition, value}) {
  if (!column || !condition) return true;
  const cell = cellValue(row, column);
  const expected = (value === null || value === undefined) ? '' : String(value);
  const text = cellText(cell).toLowerCase();
  const wanted = expected.toLowerCase();
  switch (condition) {
    case 'blank': return isBlankCell(cell);
    case 'notBlank': return !isBlankCell(cell);
    case 'equals': return text === wanted;
    case 'notEquals': return text !== wanted;
    case 'contains': return text.includes(wanted);
    case 'notContains': return !text.includes(wanted);
    case 'startsWith': return text.startsWith(wanted);
    case 'greaterThan': return !isBlankCell(cell) && compareCells(cell, expected) > 0;
    case 'lessThan': return !isBlankCell(cell) && compareCells(cell, expected) < 0;
    default: return true;
  }
}


export default {
  components: {DebouncedTextField, CustomerHeader},
  mixins: [urlState],
  mounted() {
    this.syncToUrl({
      param: 'search', urlParam: 'search', initFromRoute: true,
    })
    this.syncToUrl({
      param: 'showShopify', urlParam: 'shopify', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    })
    this.syncToUrl({
      param: 'showDeleted', urlParam: 'deleted', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    })
    this.syncToUrl({
      param: 'applyFilter', urlParam: 'filter', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    })
    this.syncToUrl({
      param: 'hasMeals', urlParam: 'has-meals', initFromRoute: true,
      parseCallback: (v) => v === 'true'
    })
    this.syncToUrl({
      param: 'daysBack', urlParam: 'days', initFromRoute: true,
      parseCallback: (v) => Number(v)
    })
    this.syncToUrl({
      param: 'filterByPlan', urlParam: 'plan', initFromRoute: true,
      // parseCallback: (v) => Number(v)
    })
    this.syncToUrl({
      param: 'usesPreferences', urlParam: 'uses', initFromRoute: true,
      transformCallback: (v) => (v || []).join(','),
      parseCallback: (v) => (v ? String(v).split(',') : []),
    })
    this.syncToUrl({
      param: 'columnFilters', urlParam: 'filters', initFromRoute: true,
      paramWatchOptions: {deep: true},
      transformCallback: (filters) => {
        const complete = this.completeColumnFilters(filters);
        return complete.length ? JSON.stringify(complete) : '';
      },
      parseCallback: (v) => {
        try {
          const parsed = v ? JSON.parse(v) : [];
          return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
          return [];
        }
      },
    })

    // this.syncToUrl({
    //   param: 'dateFrom', urlParam: 'dateFrom', initFromRoute: true
    // });
    // this.syncToUrl({
    //   param: 'dateTo', urlParam: 'dateTo', initFromRoute: true
    // });
    //
    // this.syncToUrl({
    //   param: 'firstWeekOnly', urlParam: 'new', initFromRoute: true,
    //   parseCallback: (v) => v === 'true'
    // });

    this.defaultHeaders.push(...this.headers.slice(0, 4));
    if (this.finance) {
      // we want to keep actions as the last element
      const last = this.headers.pop();
      this.headers.push(...this.financeHeaders, last);
      // keep different settings
      this.viewName = `${this.viewName}-finance`;
      this.defaultHeaders.push(...this.financeHeaders);
      this.defaultHeaders.push(this.headers.find(h => h.value === 'lastCharge'))
      this.defaultHeaders.push(this.headers.find(h => h.value === 'lastChargeMaster'))
      this.defaultHeaders.push(this.headers.find(h => h.value === 'lastChargeEither'))
      this.defaultHeaders.push(this.headers.find(h => h.value === 'invoiceCount'))
      this.defaultHeaders.push(this.headers.find(h => h.value === 'upcomingOrderCount'))
    }
    this.loadView(this.viewName);
    this.loadSavedFilters();

    if (!this.dateFrom) {
      this.dateFrom = moment().startOf('week'); //.add(1, 'week');
      if (this.finance) {
        this.dateFrom.add(1, 'week');
      }
      if (moment().day() >= 5) {
        this.dateFrom.add(1, 'week');
      }
      this.dateTo = this.dateFrom.clone().add(6, 'days');
      this.dateFrom = this.dateFrom.format(moment.HTML5_FMT.DATE);
      this.dateTo = this.dateTo.format(moment.HTML5_FMT.DATE);
    }

    return Promise.all([
      this.fetchData(),
      this.finance ? this.fetchMealFilterTitles() : null,
      this.fetchPlans()
        .then(plans => {
          this.attributeOptions.plan = Object.keys(plans);
        })
    ]);

  },
  data() {
    return {
      newStripeAccount: NEW_STRIPE_ACCOUNT,
      selectedHeaders: [],
      units: ['g', 'kg'],
      decimalPlaces: [1, 2],
      respondToRouteChanges: true,
      streamsSelected: [],
      selected: [],
      missing_meals: new Set(),
      missing_count: {},
      showDatePicker: false,
      dates: [],
      search: '',
      loading: false,
      post: null,
      error: null,
      multiview: false,
      multiviewMealSelected: {},
      financeHeaders: [
        {text: 'Stripe', value: 'stripe_id'},
        {text: 'Invoice Charges', value: 'total', align: 'right'},
        {text: 'Invoice Refunds', value: 'refunds', align: 'right'},
        {text: 'Invoice Net', value: 'net', align: 'right'},
        {text: 'First Invoice Date', value: 'firstCharge'},
        {text: 'Age (Last-First Invoice Date)', value: 'age'},
        {text: 'Old Admin', value: 'is_admin'},
        {text: 'Sub Account Admin', value: 'is_subaccount_admin'},
        {text: 'Weeks', value: 'metrics.weeks'},
        {text: 'Stream Meals', value: 'metrics.order_type.full'},
        {text: 'CC Meals', value: 'metrics.order_type.chefs_choice'},
        {text: 'Total Deliveries', value: 'metrics.shipments'},
        {text: 'MonTue Deliveries', value: 'metrics.shippingDays.mon'},
        {text: 'WedThu Deliveries', value: 'metrics.shippingDays.wed'},
        {text: 'FriSat Deliveries', value: 'metrics.shippingDays.fri'},
        {text: 'Diet Preset', value: 'dietPreset'},
        {text: 'Loved Ingredients', value: 'lovedIngredients', groupable: false},
        {text: 'Hated Ingredients', value: 'hatedIngredients', groupable: false},
        {text: 'Nutrition Goals', value: 'nutritionGoals', groupable: false},
        {text: 'initial_utm_campaign', value: 'mixpanel.initial_utm_campaign'},
        {text: 'initial_utm_term', value: 'mixpanel.initial_utm_term'},
        {text: 'initial_utm_source', value: 'mixpanel.initial_utm_source'},
        {text: 'initial_referrer', value: 'mixpanel.initial_referrer'},
        {text: 'initial_referring_domain', value: 'mixpanel.initial_referring_domain'},
        {text: 'last seen(mixpanel)', value: 'mixpanel.last_seen'}
      ],
      defaultHeaders: [],
      headers: [
        {text: 'Customer Name', value: 'name', groupable: false},
        {text: 'Email', value: 'email', width: '10px', groupable: false},
        {text: 'Phone', value: 'phone_number'},
        // {text: 'Paused', value: 'pause'},
        {text: 'Pause From', value: 'pauseFrom'},
        {text: 'Pause To', value: 'pauseTo'},
        {text: 'Plan', value: 'plan'},
        {text: 'Plan (change)', value: 'pendingEdits.plan'},
        {text: 'Coupon', value: 'coupon'},
        {text: 'Meal Size', value: 'meal_size'},
        {text: 'Delivery Times', value: 'deliveryTimes'},
        {text: 'Address', value: 'addressFormatted'},
        {text: 'Address (change)', value: 'pendingAddressFormatted'},
        {text: 'Alternate Addresses', value: 'useAddress'},
        {text: 'Production Group', value: 'production'},
        {text: 'Location', value: 'address.location'},
        {text: 'Unit', value: 'address.unit'},
        {text: 'Post Code', value: 'address.postal_code'},
        {text: 'City', value: 'address.city'},
        {text: 'Province', value: 'address.state'},
        {text: 'Use Shipping', value: 'address.use_shipping'},
        {
          text: 'Delivery Instructions',
          value: 'delivery_instruction',
          groupable: false
        },
        {text: 'Meal At Door', value: 'meal_at_door'},
        {text: 'Comment', value: 'comment', groupable: false},
        {text: 'Newsletter', value: 'newsletter'},
        {text: 'Active', value: 'is_active'},
        {text: 'Free Shipping', value: 'free_shipping'},
        {text: 'Special Request', value: 'special_request', groupable: false},
        {text: 'Company', value: 'company'},
        {text: 'User ID', value: 'uid', groupable: false},
        {text: 'Sub Accounts', value: 'subAccountCount'},
        {text: 'Paid By', value: 'paidByEmails'},
        {text: 'Dietary Restrictions', value: 'allergies'},
        {text: 'First Week', value: 'isFirstOrder'},
        {text: 'Never Ordered', value: 'neverOrdered'},
        {text: 'First Delivery Day', value: 'firstOrderDay'},
        {text: 'Invoices', value: 'invoiceCount'},
        {text: 'Found Fed Channel', value: 'foundOutBy.channel'},
        {text: 'Found Fed Detail', value: 'foundOutBy.other'},
        {text: 'Referral Credits', value: 'referralCredits'},
        {text: 'Last Invoice Date', value: 'lastCharge'},
        {text: 'Last Invoice Date (Master)', value: 'lastChargeMaster'},
        {text: 'Last Invoice Date (either)', value: 'lastChargeEither'},
        {text: 'Current Meals', value: 'currentOrderCount'},
        {text: 'Upcoming Meals', value: 'upcomingOrderCount'},
        {text: 'Extra Charge Label', value: 'price_adjustment_label'},
        {text: 'Extra Charge', value: 'price_adjustment'},
        {text: 'Signup (stripe)', value: 'stripe.created_at'},
        {text: 'Closing Balance (stripe)', value: 'stripe.closingBalance'},
        {text: 'Login Disabled', value: 'login_disabled'},
        {text: 'Actions', value: 'actions', groupable: false, width: 100}
      ],
      showMissing: false,
      filteredIngredients: [],
      expanded: [],
      footerProps: {
        itemsPerPageOptions: [10, 20, 100, 500, 1000, -1],
        showFirstLastPage: true,
      },
      viewName: 'customer-columns',
      groupBy: [],
      daysBack: 30,
      applyFilter: false,
      showBulkEditDialog: false,
      newValue: null,
      attributeToEdit: 'plan',
      savingCount: 0,
      cancelBulkEdit: null,
      customerAttributes: ['plan'],
      showEmails: false,
      attributeOptions: {},
      dateFrom: null,
      dateTo: null,
      hasMeals: true,
      includeUnconfirmed: false,
      pagingLimit: 200,
      finaliseRunning: false,
      showSnack: null,
      snackMessage: null,
      filterByPlan: null,
      ordersByCustomer: {},
      firstWeekOnly: null,
      customersRaw: [],
      showShopify: null,
      lastFetchedFields: null,
      metricHeaders: ['currentOrderCount', 'upcomingOrderCount', 'isFirstOrder', 'firstOrderDay'],
      showDeleted: null,
      fetchedWithMetrics: null,
      showColumnsDialog: false,
      columnSearch: '',
      // finance "Uses" filter: every selected flag must be set on the customer
      usesPreferences: [],
      preferenceFilterOptions: [
        {text: 'Diet Preset', value: 'hasPreset'},
        {text: 'Ingredient Preferences', value: 'hasIngredientPreferences'},
        {text: 'Nutrition Goals', value: 'hasNutritionGoals'},
      ],
      // meal filter id → title, for naming presets and nutrition goals
      mealFilterTitles: {},
      showFilterDialog: false,
      columnFilters: [],
      filterConditionOptions: FILTER_CONDITIONS,
      // named filter sets kept in localStorage per view:
      // {name: {filters: columnFilters, columns: [header values shown]}}
      savedFilters: {},
      savedFilterName: '',
      selectedSavedFilter: null,
    }
  },
  watch: {
    showShopify() {
      return this.fetchData()
    },
    hasMeals() {
      // need to refetch customer data with hasUpcoming meals in it in it
      this.fetchData()
    },
    showColumnsDialog(open) {
      // Columns drive which fields are fetched. Apply the change only when the
      // column dialog closes (Done / X / click-away), not on every checkbox
      // toggle, and only refetch if the required field set actually changed
      // (eg. no refetch when a toggled column is already covered by another).
      if (!open && this.customerFieldsParam() !== this.lastFetchedFields) {
        this.fetchData();
      }
    },
    columnFilters: {
      deep: true,
      handler(filters) {
        const saved = this.selectedSavedFilter && this.savedFilters[this.selectedSavedFilter];
        if (saved && JSON.stringify(saved.filters) !== JSON.stringify(this.completeColumnFilters(filters))) {
          this.selectedSavedFilter = null;
        }
      },
    },
    showFilterDialog(open) {
      // A filtered column needs its field fetched even when it is not shown.
      if (!open && this.customerFieldsParam() !== this.lastFetchedFields) {
        this.fetchData();
      }
    },
  },
  computed: {
    ...mapGetters([
      'diets',
      'getDietName',
      'getDietStream',
      'getRestrictionText',
      'getOrdersOnDate',
    ]),
    ordersUnfiltered() {
      const orders = [];
      console.log('concat orders from dates', {dates: this.dates});
      this.getDatesInRange()
        .forEach(date =>
          orders.push(...(
            this.getOrdersOnDate({date}) || []
          )));
      return orders;
    },
    sortedHeaders() {
      const headers = this.headers.filter(h => this.selectedHeaders.find(i => i.value === h.value));
      return headers;
    },
    readOnly() {
      return !!this.$route.meta.readOnly;
    },
    customersUnfiltered() {
      if (this.loading) {
        console.log('still loading');
        return [];
      }
      const customers = this.customersRaw;
      console.log('done loading', customers.length);
      const formatCustomer = (c, isSub = false) => {
        // console.log('isSub', c.uid, isSub);
        const pendingAddress = c.pendingEdits && c.pendingEdits.address;
        const subCustomers = isSub === true // to avoid cycles in formatCustomer, we pass isSub=true
          ? []
          : Object.keys(c.subAccounts || {})
            .map(uid => customers.find(c => c.uid === uid))
            .filter(c => !!c)
            .map(c => formatCustomer(c, true));
        const isFirstOrder = c.isFirstOrder; //(this.ordersByCustomer[c.uid] || []).some((o) => o.isFirstOrder);
        const totalReferralCredit = (c.referralCoupons || []).map(c => c.credit).reduce((sum, i) => sum + (i || 0), 0);
        const referralCredits = (totalReferralCredit / 100).toFixed(2);
        const age = c.stats && c.stats.firstCharge ? Math.abs(moment(c.stats.firstCharge).diff(c.stats.lastCharge, 'days')) : '';
        let firstOrder = isFirstOrder ? 'yes!' : 'no!';
        if (c.stripe && c.stripe.created_at && firstOrder !== 'yes!') {
          if (moment(c.stripe.created_at).isAfter(this.dateFrom) && c.upcomingOrderCount > 0) {
            firstOrder = 'new!';
          }
        }
        return {
          ...c,
          ...(c.stats ? c.stats : {}),
          age,
          deliveryTimes: this.formatDeliveryTime(c.address),
          name: `${c.first_name} ${c.last_name}`.trim(),
          referralCredits,
          firstOrderDay: c.isFirstOrder ? `${moment(c.isFirstOrder).format('ddd')}!` : '',
          addressFormatted: formatAddress(c.address),
          pendingAddressFormatted: !pendingAddress ? '' : formatAddress(pendingAddress),
          ...Object.fromEntries(
            Object.keys(c.address)
              .map(k => ['address.' + k, c.address[k]])
          ),
          coupon: c.stripe && c.stripe.coupon ? c.stripe.coupon : c.coupon,
          subCustomers: subCustomers,
          subAccountCount: subCustomers.length,
          allergies: c.allergies ? c.allergies.map(a => this.getRestrictionText(a)) : [],
          stripe_id: c.stripe ? c.stripe.id : '',
          // upcomingOrderCount: (this.ordersByCustomer[c.uid] || []).reduce((a, i) => i.quantity + a, 0),
          isFirstOrder: firstOrder,
          neverOrdered: !(c.metrics && c.metrics.weeks),
          pauseFrom: c.pause ? c.pause[0] : '',
          pauseTo: c.pause ? c.pause[1] : '',
          ...this.formatPreferences(c),
        }
      }
      const result = customers
        .filter(c => c.address) //customer with no address is junk!
        .map(formatCustomer);

      const paidBy = {};
      result
        .filter(c => c.subAccounts)
        .forEach(c => {
          Object
            .keys(c.subAccounts)
            .forEach(uid => {
              paidBy[uid] = paidBy[uid] || [];
              paidBy[uid].push(c);
            })
        })
      result.forEach(c => {
        const paidByCustomer = paidBy[c.uid] || [];
        c.paidBy = paidByCustomer;
        c.paidByEmails = paidByCustomer.map(c => c.email).join(', ');
        c.lastChargeMaster = paidByCustomer.map(c => c.lastCharge).join(', ');
        c.lastChargeEither = [c.lastCharge, ...paidByCustomer.map(c => c.lastCharge)]
          .filter(s => !!s)
          .sort()
          .reverse()[0];
      });
      // console.log('cusotmersUnfiltered', result);
      return result;
    },
    customers() {
      let customers = this.customersUnfiltered;
      if (!this.showDeleted) {
        customers = customers.filter(c => !c.isDeleted);
      }
      if (this.firstWeekOnly) {
        customers = customers.filter(c => c.isFirstOrder);
      }
      const filterDaysBack = (c) => {
        if (this.hasMeals) {
          return c.upcomingOrderCount > 0 || c.currentOrderCount > 0;
        }
        if (this.applyFilter) {
          const lastCharge = c.stats && c.stats.lastCharge;
          if (lastCharge) {
            const daysSince = moment.duration(moment().diff(moment(lastCharge))).asDays();
            // console.log('filter', [lastCharge, daysSince]);
            return daysSince <= this.daysBack;
          } else {
            return false;
          }
        } else
          return true;
      }

      if (this.filterByPlan) {
        customers = customers.filter(c => c.plan === this.filterByPlan);
      }
      if (this.usesPreferences.length) {
        customers = customers.filter(c => this.usesPreferences.every(flag => c[flag]));
      }
      if (this.columnFilters.length) {
        customers = customers.filter(c => this.columnFilters.every(f => rowMatchesFilter(c, f)));
      }
      return customers.filter(filterDaysBack);
    },
    totalCount() {
      let count = 0;
      this.customers.forEach(o => count += o.quantity);
      return count;
    },
    totalMealCount() {
      let count = 0;
      this.customers.forEach(o => count += (o.upcomingOrderCount || 0));
      return count;
    },
    views() {
      return [];
    },
    plans() {
      return [...new Set(this.customersUnfiltered.map(c => c.plan))].sort();
    },
    upcomingDateFrom() {
      return this.dateFrom
        ? moment(this.dateFrom).add(1, 'week').format(moment.HTML5_FMT.DATE)
        : '';
    },
    upcomingDateTo() {
      return this.dateTo
        ? moment(this.dateTo).add(1, 'week').format(moment.HTML5_FMT.DATE)
        : '';
    },
    metricsShown() {
      return this.hasMeals || this.selectedHeaders.some(h => this.metricHeaders.includes(h.value));
    },
    dataAsTable() {
      return csvAsTable(this.customers, this.sortedHeaders, this.search);
    },
    savedFilterNames() {
      return Object.keys(this.savedFilters).sort((a, b) => a.localeCompare(b));
    },
    canSaveFilters() {
      return this.savedFilterName.trim() !== '' && this.completeColumnFilters(this.columnFilters).length > 0;
    },
    filterColumnOptions() {
      return this.headers
        .filter(h => h.value !== 'actions')
        .map(h => ({text: h.text, value: h.value}))
        .sort((a, b) => a.text.localeCompare(b.text));
    },
    filteredColumnHeaders() {
      if (!this.columnSearch) return this.headers;
      const search = this.columnSearch.toLowerCase();
      return this.headers.filter(h => h.text.toLowerCase().includes(search));
    },
  },
  methods: {
    formatAddress,
    ...mapActions([
      'fetchRestrictions',
      'fetchPlans',
      'fetchOrdersByDate',
    ]),
    dateFormatted,
    getDatesInRange() {
      const dates = [];
      if (!this.dateFrom || !this.dateTo) {
        return dates;
      }
      const from = moment(this.dateFrom);
      const to = moment(this.dateTo);

      const diff = moment.duration(to.diff(from));
      const maxDays = 31;
      if (diff.asDays() >= 0 && diff.asDays() <= maxDays) {
        // console.log('adding days ', {from, to})
        while (from.isSameOrBefore(to)) {
          const dateString = getDateString(from);
          // console.log('added date', dateString);
          dates.push(dateString);
          from.add(1, 'day');
        }
      } else {
        window.alert(diff.asDays() + ' days is above the limit of ' + maxDays);
      }
      return dates;
    },
    fetchData() {
      this.error = this.post = null
      this.loading = true;
      return Promise.all([
        this.fetchCustomers2(),
        this.fetchRestrictions(),
      ])
        .catch((e) => {
          console.error('something went wrong', e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    meal_sizes() {
      return 'small,medium,large'.split(',');
    },
    saveSettings() {
      const selectedHeaders = this.selectedHeaders;
      localStorage.setItem(this.viewName, JSON.stringify(selectedHeaders));
    },
    loadView(viewName) {
      try {
        this.selectedHeaders = JSON.parse(localStorage.getItem(viewName));
        if (this.viewName !== viewName) {
          this.viewName = viewName;
        }
      } catch (e) {
        console.warn('failed to restore ', viewName);
      }
      if (!this.selectedHeaders) {
        this.selectedHeaders = this.defaultHeaders;
      }
    },
    async saveBulkEdit() {
      const message = `Are you sure you want to change ${this.selected.length} customers' ${this.attributeToEdit} to ${this.newValue}?`
      if (confirm(message)) {
        const update = {};
        this.cancelBulkEdit = false;
        this.savingCount = this.selected.length;
        update[this.attributeToEdit] = this.newValue;
        for (const customer of this.selected) {
          const {uid} = customer;
          if (!this.cancelBulkEdit) {
            await api.put(`users/firebase/${uid}`, update)
              .then(() => {
                customer[this.attributeToEdit] = this.newValue;
                this.savingCount--;
              })
          }
        }
      }
      this.showBulkEditDialog = false;
    },
    copyEmails() {
      let textToCopy = this.$refs.emailsToCopy.$el.querySelector('textarea')
      textToCopy.select()
      document.execCommand("copy");
    },
    async finaliseInvoices() {
      if (confirm('all draft invoices created within the last day will be finalised')) {
        this.finaliseRunning = true;
        let result = await api.post('v2/stripe/finalise-all');
        this.showSnack = true;
        this.snackMessage = result.data.progress;
        while (result && result.data.progress !== 'stopped') {
          result = await api.post('v2/stripe/finalise-all?check=true');
          this.showSnack = true;
          this.snackMessage = result && result.data.progress;
          await new Promise(resolve => {
            setTimeout(() => resolve(1), 1000)
          });
        }
        this.finaliseRunning = false;
        alert('invoices finalised');
      }
    },
    adjustDate(amount, unit) {
      this.dateFrom = moment(this.dateFrom).add(amount, unit).format(moment.HTML5_FMT.DATE);
      this.dateTo = moment(this.dateTo).add(amount, unit).format(moment.HTML5_FMT.DATE);
      return this.fetchData();
    },
    // The comma-separated field projection for the current visible columns,
    // unioned with the always-required set. Sent as a string (not an array) so
    // it can't trip qs's default arrayLimit of 20, which would otherwise mangle
    // the projection. Metric columns are populated via orderMetrics/hasMeals.
    customerFieldsParam() {
      return [...new Set([
        ...ALWAYS_REQUIRED_FIELDS,
        ...(this.finance ? ['ingredient_preferences'] : []),
        ...this.sortedHeaders.flatMap(h => columnToFields(h.value)),
        ...this.columnFilters.filter(f => f.column).flatMap(f => columnToFields(f.column)),
      ])].join(',');
    },
    fetchCustomers2() {
      const fields = this.customerFieldsParam();
      this.lastFetchedFields = fields;
      return api
        .get('v2/customers', {
          params: {
            skipShopify: !this.showShopify,
            orderMetrics: this.metricsShown,
            hasMeals: this.hasMeals,
            includeUnconfirmed: this.includeUnconfirmed,
            fields,
          }
        })
        .then(({data}) => {
          this.customersRaw = data;
        });
    },
    selectAllColumns() {
      this.selectedHeaders = [...this.headers];
      this.saveSettings();
    },
    clearAllColumns() {
      this.selectedHeaders = [];
      this.saveSettings();
    },
    toggleColumn(header, isChecked) {
      if (isChecked) {
        if (!this.selectedHeaders.some(h => h.value === header.value)) {
          this.selectedHeaders = [...this.selectedHeaders, header];
        }
      } else {
        this.selectedHeaders = this.selectedHeaders.filter(h => h.value !== header.value);
      }
      this.saveSettings();
    },
    csvExport,
    addColumnFilter() {
      this.columnFilters.push({column: null, condition: 'notBlank', value: ''});
    },
    // filters with a column, a condition and (where needed) a value
    completeColumnFilters(filters) {
      return (filters || [])
        .filter(f => f.column && f.condition && (!this.filterConditionNeedsValue(f.condition) || f.value))
        .map(({column, condition, value}) => ({column, condition, value: value || ''}));
    },
    savedFiltersStorageKey() {
      return `${this.viewName}-saved-filters`;
    },
    loadSavedFilters() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.savedFiltersStorageKey()) || '{}');
        const valid = stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
        this.savedFilters = Object.fromEntries(Object.entries(valid).map(([name, saved]) => [
          name,
          // a bare array is the first format, filters only
          Array.isArray(saved) ? {filters: saved, columns: null} : saved,
        ]));
      } catch (e) {
        this.savedFilters = {};
      }
    },
    persistSavedFilters() {
      localStorage.setItem(this.savedFiltersStorageKey(), JSON.stringify(this.savedFilters));
    },
    saveCurrentFilters() {
      if (!this.canSaveFilters) return;
      const name = this.savedFilterName.trim();
      this.$set(this.savedFilters, name, {
        filters: this.completeColumnFilters(this.columnFilters),
        columns: this.selectedHeaders.map(h => h.value),
      });
      this.persistSavedFilters();
      this.savedFilterName = '';
      this.selectedSavedFilter = name;
    },
    applySavedFilter(name) {
      if (!name) {
        // the toolbar dropdown was cleared
        this.selectedSavedFilter = null;
        this.columnFilters = [];
        return;
      }
      const saved = this.savedFilters[name];
      if (!saved) return;
      this.selectedSavedFilter = name;
      this.columnFilters = (saved.filters || []).map(f => ({...f}));
      if (Array.isArray(saved.columns)) {
        this.showColumns(saved.columns);
      }
    },
    // make exactly these header values visible (unknown values ignored) and
    // refetch if that needs fields not loaded yet
    showColumns(values) {
      const headers = values.map(v => this.headers.find(h => h.value === v)).filter(h => !!h);
      if (!headers.length) return;
      this.selectedHeaders = headers;
      this.saveSettings();
      if (this.customerFieldsParam() !== this.lastFetchedFields) {
        this.fetchData();
      }
    },
    deleteSavedFilter(name) {
      this.$delete(this.savedFilters, name);
      this.persistSavedFilters();
      if (this.selectedSavedFilter === name) {
        this.selectedSavedFilter = null;
      }
    },
    filterConditionNeedsValue(condition) {
      const found = FILTER_CONDITIONS.find(c => c.value === condition);
      return !!(found && found.needsValue);
    },
    fetchMealFilterTitles() {
      return api.get('v2/meal-filter')
        .then(({data}) => {
          this.mealFilterTitles = Object.fromEntries((data || []).map(f => [f.id, f.title || f.id]));
        })
        .catch(e => console.warn('could not load meal filter titles', e));
    },
    // The customer's ACTIVE diet preset, loved/hated ingredients and nutrition
    // goals (a pending edit replaces the saved ingredient_preferences wholesale,
    // as fed-lib's getUsersActiveIngredientPreference does) plus the has* flags
    // the "Uses" filter reads.
    formatPreferences(c) {
      const pending = c.pendingEdits && c.pendingEdits.ingredient_preferences;
      const prefs = (pending !== undefined ? pending : c.ingredient_preferences) || {};
      const listOf = (v) => Array.isArray(v) ? v.filter(x => !!x) : [];
      const presets = listOf(prefs.meal_presets).map(id => this.mealFilterTitles[id] || id);
      const loved = listOf(prefs.love);
      const hated = listOf(prefs.hate);
      // `filters` is the current goals list; `nutrient_preferences` the legacy one
      const goals = [...listOf(prefs.filters), ...listOf(prefs.nutrient_preferences)]
        .map(goal => this.formatNutritionGoal(goal));
      const uniqueGoals = [...new Set(goals)];
      return {
        dietPreset: presets.join(', '),
        lovedIngredients: loved,
        hatedIngredients: hated,
        nutritionGoals: uniqueGoals,
        hasPreset: presets.length > 0,
        hasIngredientPreferences: loved.length > 0 || hated.length > 0,
        hasNutritionGoals: uniqueGoals.length > 0,
      };
    },
    // "More Protein" / "Less Sodium" from a goal's `<nutrient>-max|-min` filter id
    formatNutritionGoal(goal) {
      const filter = (goal && goal.filter) || {};
      const filterId = String(filter.id || '');
      const direction = (filterId.match(/-(max|min)$/) || [])[1];
      const nutrientId = direction ? filterId.slice(0, -(direction.length + 1)) : filterId;
      const name = this.mealFilterTitles[filterId] || this.mealFilterTitles[nutrientId]
        || filter.title || nutrientId;
      if (!direction) return name;
      return `${direction === 'max' ? 'More' : 'Less'} ${name}`;
    },
    formatDeliveryTime({earliest_time, latest_time}) {
      if (!earliest_time && !latest_time) {
        return '';
      }
      if (earliest_time && latest_time) {
        return `${earliest_time} to ${latest_time}`;
      }
      if (earliest_time) {
        return `after ${earliest_time}`;
      }
      if (latest_time) {
        return `before ${latest_time}`;
      }
    }
  },
  props: {
    finance: {type: Boolean, default: false, required: false},
    canBulkEdit: {type: Boolean, default: false, required: false}
  }
}
</script>

<style>
.v-data-table > .v-data-table__wrapper tbody tr.v-data-table__expanded__content {
  box-shadow: none !important;
}
</style>
